"use client"

import { PageLayout } from "@/components/page-layout"

export default function DocumentationPage() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Documentation</h1>
          <p className="text-muted-foreground">
            Understanding Discount Calculations
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Overview</h2>
          <p className="text-card-foreground leading-relaxed">
            Any delay in liquidation, caused by regulatory restraint (marketability), or lack of buyers at the current price (illiquidity) creates price risk for the holder. This price risk results in a lower price offered for stocks subject to a delayed liquidation. Larger blocks require longer periods to liquidate and are likely to incur significant blockage discounts.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Liquidation Period Estimation</h2>
          <p className="text-card-foreground leading-relaxed">
            I estimate the expected period of liquidation using the number of shares outstanding and the observed trading volume. The first set of blockage discounts presented below are based on this liquidation period and the observed volatility for the stocks. These are applicable when there are no regulatory restraints on liquidation.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Restricted Stock Analysis</h2>
          <p className="text-card-foreground leading-relaxed">
            Restricted stock is identical to the unrestricted stock in all respects, except for the ability to trade during the period of restriction. In the absence of any market frictions, this delayed liquidation, made necessary by a regulatory restraint, (marketability) exposes the holder to a larger price risk. Once the period of restriction ends, a liquidation period, estimated as above, starts. The combined discounts for trading restriction (marketability) and blockage are presented in the second set of tables.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Theoretical Foundation</h2>
          <p className="text-card-foreground leading-relaxed mb-4">
            Both of these sets of discounts are based on the Margrabe exchange option equation. Margrabe exchange option formula assumes that the buyer and seller are equally informed with all public information. In the presence of additional private information, two extensions of this analysis can exist:
          </p>
          <ul className="list-disc list-inside space-y-2 text-card-foreground ml-4">
            <li><strong>Asian Average option models</strong> are applicable when the buyer and the seller both have access to all information, public and private.</li>
            <li><strong>Look back option models</strong> allow for the seller to have access to private information, in addition to all public information available to the buyer creating information asymmetry.</li>
          </ul>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">Rule 144 Holding Period Requirements</h2>
          <p className="text-card-foreground leading-relaxed mb-4">
            Rule 144 holding period requirements are incorporated in the lack of marketability analysis as follows:
          </p>
          <div className="space-y-3">
            <div className="flex items-center space-x-4 p-3 bg-muted rounded-lg">
              <span className="font-semibold text-foreground">After March 2008:</span>
              <span className="text-muted-foreground">Six months holding period (+ 126 days to liquidation period)</span>
            </div>
            <div className="flex items-center space-x-4 p-3 bg-muted rounded-lg">
              <span className="font-semibold text-foreground">Before March 2008:</span>
              <span className="text-muted-foreground">One year holding period (+ 252 days to liquidation period)</span>
            </div>
            <div className="flex items-center space-x-4 p-3 bg-muted rounded-lg">
              <span className="font-semibold text-foreground">Before February 1997:</span>
              <span className="text-muted-foreground">Two year holding period (+ 504 days to liquidation period)</span>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
